import { useBrandex } from "@brandex/dashboard";
import { formatDateTime } from "@brandex/i18n";
import { Alert, Badge, Button, Card, CardContent, CardHeader, CardTitle, Field, Input, Select, Spinner, Textarea } from "@brandex/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ExternalLink, Plus, Trash2 } from "lucide-react";
import { useState, type ReactNode } from "react";

import { cmsKeys, useSiteEditor, type SiteLocale, type SiteSections } from "../data";
import { QueryError } from "../views";

const ICONS = ["studio", "portfolio", "booking", "media", "approve", "report", "chat", "growth"] as const;
const WEB_URL = (import.meta.env.VITE_WEB_BASE_URL as string | undefined) ?? "http://localhost:3000";

/** Moves item `i` by `delta` within a copy of `list`. */
export function move<T>(list: T[], i: number, delta: number): T[] {
  const j = i + delta;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j] as T, next[i] as T];
  return next;
}

/** Public site CMS (F-03): edit the draft per locale, then publish. */
export function CmsPage() {
  const [locale, setLocale] = useState<SiteLocale>("en");
  const editor = useSiteEditor(locale);
  return (
    <div className="grid gap-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Site content</h1>
          <p className="mt-1 text-sm text-muted-foreground">Landing page copy. The price table comes from the pinned plan (Packages).</p>
        </div>
        <div role="radiogroup" aria-label="Language" className="inline-flex rounded-[var(--radius-pill)] border border-border p-0.5">
          {(["en", "bn"] as const).map((l) => (
            <button key={l} type="button" role="radio" aria-checked={locale === l} onClick={() => setLocale(l)} className={`rounded-full px-4 py-1 text-sm ${locale === l ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
              {l === "en" ? "English" : "বাংলা"}
            </button>
          ))}
        </div>
      </header>
      {editor.isPending ? <Spinner label="Loading…" /> : null}
      {editor.isError ? <QueryError error={editor.error} /> : null}
      {editor.data !== undefined ? (
        <Editor
          key={`${locale}-${String(editor.data.draft?.version ?? 0)}`}
          locale={locale}
          initial={editor.data.draft?.sections ?? editor.data.published?.sections ?? null}
          draftVersion={editor.data.draft?.version ?? null}
          publishedVersion={editor.data.published?.version ?? null}
          updatedAt={editor.data.draft?.updated_at ?? null}
        />
      ) : null}
    </div>
  );
}

const EMPTY: SiteSections = {
  hero: { badge: "", title: "", body: "", primary_cta: "", secondary_cta: "" },
  features: { title: "", subtitle: "", items: [{ icon: "studio", title: "", body: "" }] },
  breakdown: { title: "Why {price} covers {value} of work", footnote: "" },
  how_it_works: { title: "", steps: [""] },
  cta: { title: "", body: "", button: "" },
  footer_tagline: "",
};

function Editor(props: { locale: SiteLocale; initial: SiteSections | null; draftVersion: number | null; publishedVersion: number | null; updatedAt: string | null }) {
  const { api } = useBrandex();
  const qc = useQueryClient();
  const [s, setS] = useState<SiteSections>(props.initial ?? EMPTY);
  const [dirty, setDirty] = useState(false);
  const update = (next: SiteSections) => {
    setS(next);
    setDirty(true);
  };
  const save = useMutation({
    mutationFn: async () => {
      await api.http.PUT("/v1/admin/cms/site", { params: { query: { locale: props.locale } }, body: s });
    },
    onSuccess: () => {
      setDirty(false);
      void qc.invalidateQueries({ queryKey: cmsKeys.site(props.locale) });
    },
  });
  const publish = useMutation({
    mutationFn: async () => {
      await api.http.POST("/v1/admin/cms/site/publish", { params: { query: { locale: props.locale } } });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: cmsKeys.site(props.locale) }),
  });
  const unpublished = props.draftVersion !== null && props.draftVersion !== props.publishedVersion;

  return (
    <div className="grid gap-6">
      <div className="glass sticky top-4 z-10 flex flex-wrap items-center gap-3 rounded-[var(--radius-lg)] p-3">
        <span className="text-sm text-muted-foreground">
          Draft v{props.draftVersion ?? "—"} · Live v{props.publishedVersion ?? "—"}
          {props.updatedAt !== null ? ` · saved ${formatDateTime("en", props.updatedAt)}` : ""}
        </span>
        {dirty ? <Badge tone="warning">Unsaved edits</Badge> : unpublished ? <Badge tone="warning">Draft not published</Badge> : <Badge tone="success">Live matches draft</Badge>}
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="ghost" asChild>
            <a href={props.locale === "en" ? WEB_URL : `${WEB_URL}/bn`} target="_blank" rel="noreferrer">View site <ExternalLink className="h-3.5 w-3.5" aria-hidden /></a>
          </Button>
          <Button size="sm" variant="outline" disabled={!dirty} loading={save.isPending} onClick={() => save.mutate()}>Save draft</Button>
          <Button size="sm" disabled={dirty || !unpublished} loading={publish.isPending} onClick={() => publish.mutate()}>Publish</Button>
        </div>
      </div>
      {save.isError ? <QueryError error={save.error} /> : null}
      {publish.isError ? <QueryError error={publish.error} /> : null}
      {publish.isSuccess && !unpublished ? <Alert tone="success">Published. The site updates within a minute.</Alert> : null}

      <Section title="Hero">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Badge"><Input value={s.hero.badge} onChange={(e) => update({ ...s, hero: { ...s.hero, badge: e.target.value } })} /></Field>
          <Field label="Title"><Input value={s.hero.title} onChange={(e) => update({ ...s, hero: { ...s.hero, title: e.target.value } })} /></Field>
        </div>
        <Field label="Body"><Textarea rows={3} value={s.hero.body} onChange={(e) => update({ ...s, hero: { ...s.hero, body: e.target.value } })} /></Field>
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Primary button"><Input value={s.hero.primary_cta} onChange={(e) => update({ ...s, hero: { ...s.hero, primary_cta: e.target.value } })} /></Field>
          <Field label="Secondary button"><Input value={s.hero.secondary_cta} onChange={(e) => update({ ...s, hero: { ...s.hero, secondary_cta: e.target.value } })} /></Field>
        </div>
      </Section>

      <Section title="What you get">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Section title"><Input value={s.features.title} onChange={(e) => update({ ...s, features: { ...s.features, title: e.target.value } })} /></Field>
          <Field label="Subtitle"><Input value={s.features.subtitle} onChange={(e) => update({ ...s, features: { ...s.features, subtitle: e.target.value } })} /></Field>
        </div>
        {s.features.items.map((f, i) => {
          const setItem = (patch: Partial<typeof f>) => update({ ...s, features: { ...s.features, items: s.features.items.map((x, j) => (j === i ? { ...x, ...patch } : x)) } });
          return (
            <div key={i} className="grid gap-3 rounded-md border border-border p-4 md:grid-cols-[10rem_1fr_auto]">
              <Field label={`Card ${String(i + 1)} icon`}>
                <Select value={f.icon} onChange={(e) => setItem({ icon: e.target.value })}>
                  {ICONS.map((icon) => <option key={icon} value={icon}>{icon}</option>)}
                </Select>
              </Field>
              <div className="grid gap-3">
                <Field label={`Card ${String(i + 1)} title`}><Input value={f.title} onChange={(e) => setItem({ title: e.target.value })} /></Field>
                <Field label={`Card ${String(i + 1)} text`}><Textarea rows={2} value={f.body} onChange={(e) => setItem({ body: e.target.value })} /></Field>
              </div>
              <ListControls
                onUp={() => update({ ...s, features: { ...s.features, items: move(s.features.items, i, -1) } })}
                onDown={() => update({ ...s, features: { ...s.features, items: move(s.features.items, i, 1) } })}
                onRemove={s.features.items.length > 1 ? () => update({ ...s, features: { ...s.features, items: s.features.items.filter((_, j) => j !== i) } }) : undefined}
              />
            </div>
          );
        })}
        {s.features.items.length < 12 ? (
          <div><Button size="sm" variant="outline" onClick={() => update({ ...s, features: { ...s.features, items: [...s.features.items, { icon: "studio", title: "", body: "" }] } })}><Plus className="h-4 w-4" aria-hidden />Add card</Button></div>
        ) : null}
      </Section>

      <Section title="Price table (values come from the pinned plan)">
        <Field label="Heading" hint="Use {price} and {value} for the plan price and market value.">
          <Input value={s.breakdown.title} onChange={(e) => update({ ...s, breakdown: { ...s.breakdown, title: e.target.value } })} />
        </Field>
        <Field label="Footnote"><Textarea rows={2} value={s.breakdown.footnote} onChange={(e) => update({ ...s, breakdown: { ...s.breakdown, footnote: e.target.value } })} /></Field>
      </Section>

      <Section title="How it works">
        <Field label="Section title"><Input value={s.how_it_works.title} onChange={(e) => update({ ...s, how_it_works: { ...s.how_it_works, title: e.target.value } })} /></Field>
        {s.how_it_works.steps.map((step, i) => (
          <div key={i} className="flex items-end gap-2">
            <Field label={`Step ${String(i + 1)}`} className="flex-1">
              <Input value={step} onChange={(e) => update({ ...s, how_it_works: { ...s.how_it_works, steps: s.how_it_works.steps.map((x, j) => (j === i ? e.target.value : x)) } })} />
            </Field>
            <ListControls
              onUp={() => update({ ...s, how_it_works: { ...s.how_it_works, steps: move(s.how_it_works.steps, i, -1) } })}
              onDown={() => update({ ...s, how_it_works: { ...s.how_it_works, steps: move(s.how_it_works.steps, i, 1) } })}
              onRemove={s.how_it_works.steps.length > 1 ? () => update({ ...s, how_it_works: { ...s.how_it_works, steps: s.how_it_works.steps.filter((_, j) => j !== i) } }) : undefined}
            />
          </div>
        ))}
        {s.how_it_works.steps.length < 12 ? (
          <div><Button size="sm" variant="outline" onClick={() => update({ ...s, how_it_works: { ...s.how_it_works, steps: [...s.how_it_works.steps, ""] } })}><Plus className="h-4 w-4" aria-hidden />Add step</Button></div>
        ) : null}
      </Section>

      <Section title="Closing call to action and footer">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Title"><Input value={s.cta.title} onChange={(e) => update({ ...s, cta: { ...s.cta, title: e.target.value } })} /></Field>
          <Field label="Button"><Input value={s.cta.button} onChange={(e) => update({ ...s, cta: { ...s.cta, button: e.target.value } })} /></Field>
        </div>
        <Field label="Text"><Input value={s.cta.body} onChange={(e) => update({ ...s, cta: { ...s.cta, body: e.target.value } })} /></Field>
        <Field label="Footer tagline"><Input value={s.footer_tagline} onChange={(e) => update({ ...s, footer_tagline: e.target.value })} /></Field>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card>
      <CardHeader><CardTitle>{title}</CardTitle></CardHeader>
      <CardContent className="grid gap-4">{children}</CardContent>
    </Card>
  );
}

function ListControls({ onUp, onDown, onRemove }: { onUp: () => void; onDown: () => void; onRemove?: (() => void) | undefined }) {
  return (
    <div className="flex items-start gap-1">
      <Button size="icon" variant="ghost" aria-label="Move up" onClick={onUp}><ArrowUp className="h-4 w-4" aria-hidden /></Button>
      <Button size="icon" variant="ghost" aria-label="Move down" onClick={onDown}><ArrowDown className="h-4 w-4" aria-hidden /></Button>
      {onRemove ? <Button size="icon" variant="ghost" aria-label="Remove" onClick={onRemove}><Trash2 className="h-4 w-4" aria-hidden /></Button> : null}
    </div>
  );
}
